import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Music, ChevronRight } from 'lucide-react';
import { useFullscreen } from '@/contexts/FullscreenContext';

/**
 * JourneyTherapyPreview - ÉCRAN 8 : Aperçu Paysages sonores avec Spectrogramme
 * Spectrogramme animé visuellement (audio joué depuis JourneyIntro)
 */
const JourneyTherapyPreview = () => {
  const navigate = useNavigate();
  const { setIsFullscreen } = useFullscreen();
  const [showContent, setShowContent] = useState(false);
  const [bars, setBars] = useState([]);
  
  const isEnglish = localStorage.getItem('i18nextLng')?.includes('en') ||
                    localStorage.getItem('hearen_lang') === 'en';

  useEffect(() => {
    setIsFullscreen(true);
    
    // Générer les barres du spectrogramme avec activation progressive
    const barsData = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      height: 20 + Math.random() * 65,
      delay: i * 0.02, // Activation progressive échelonnée
    }));
    setBars(barsData);
    
    // Fade-in progressif du contenu
    setTimeout(() => setShowContent(true), 300);
    
    return () => {
      setIsFullscreen(false);
    };
  }, [setIsFullscreen]);

  const handleContinue = () => {
    console.log('🎵 JourneyTherapyPreview: Navigating to /journey-progress-preview');
    navigate('/journey-progress-preview');
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] pb-32 relative overflow-hidden flex flex-col items-center justify-center px-6">
      {/* Background gradient */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.3) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Contenu principal - Fade-in + Translation verticale */}
      <div
        className="relative z-10 max-w-md w-full transition-all duration-700 ease-out"
        style={{
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(30px)',
        }}
      >
        {/* Bandeau pédagogique premium */}
        <div 
          className="mb-8 px-6 py-4 rounded-2xl"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(219, 39, 119, 0.15) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
          }}
        >
          <div className="flex items-start gap-3">
            <Music className="w-6 h-6 text-purple-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white/90 text-sm leading-relaxed">
                {isEnglish
                  ? 'Discover Hearen sound landscapes. Deep, instant calm.'
                  : 'Découvrez les paysages sonores Hearen. Apaisement profond, instantané.'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Player Paysages sonores Premium (Aperçu) */}
        <div
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 mb-8"
          style={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              {isEnglish ? 'Ambient Soundscape' : 'Paysage Sonore Ambient'}
            </h2>
            <p className="text-purple-400 text-sm">
              {isEnglish ? 'Soft ambiances' : 'Ambiances douces'}
            </p>
          </div>

          {/* Spectrogramme Hearen Premium - Barres fines, dégradés verticaux, fluide */}
          <div 
            className="flex items-end justify-center gap-0.5 h-40 mb-6 px-2"
            style={{
              animation: 'spectro-subtle-pulse 3s ease-in-out infinite',
            }}
          >
            {bars.map((bar) => (
              <div
                key={bar.id}
                className="rounded-t-sm transition-all"
                style={{
                  width: '3px', // Barres très fines
                  height: `${bar.height}%`,
                  background: 'linear-gradient(to top, rgba(16, 185, 129, 0.9) 0%, rgba(16, 185, 129, 0.6) 50%, rgba(16, 185, 129, 0.3) 100%)', // Dégradé vertical vert
                  animation: `bar-organic-flow ${2 + Math.random() * 2}s ease-in-out infinite`,
                  animationDelay: `${bar.delay}s`,
                  boxShadow: '0 0 4px rgba(16, 185, 129, 0.3)',
                }}
              />
            ))}
          </div>

          {/* Progress bar désactivée */}
          <div className="mb-4 opacity-50">
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                style={{ width: '35%' }}
              />
            </div>
            <div className="flex justify-between text-xs text-white/70 mt-2">
              <span>2:15</span>
              <span>6:30</span>
            </div>
          </div>

          {/* Badge statique - l'audio est déjà en cours depuis JourneyIntro */}
          <div className="text-center mt-2 text-purple-400/80 text-sm">
            {isEnglish ? 'Preview' : 'Aperçu'}
          </div>
        </div>

        {/* CTA Continuer */}
        <div className="flex justify-center">
          <button
            onClick={handleContinue}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-lg transition-all hover:scale-105 active:scale-95 text-white"
            style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #db2777 100%)',
              boxShadow: '0 8px 30px rgba(139, 92, 246, 0.4)',
            }}
          >
            {isEnglish ? 'Continue' : 'Continuer'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Animations CSS - Style Hearen Premium */}
      <style>{`
        /* Mouvement fluide et organique des barres - vitesse lente, easing doux */
        @keyframes bar-organic-flow {
          0%, 100% { 
            transform: scaleY(1); 
            opacity: 0.8; 
          }
          33% { 
            transform: scaleY(1.15); 
            opacity: 0.95; 
          }
          66% { 
            transform: scaleY(0.9); 
            opacity: 0.85; 
          }
        }
        
        /* Pulse subtil du spectrogramme */
        @keyframes spectro-subtle-pulse {
          0%, 100% { 
            opacity: 0.95; 
            transform: scale(1); 
          }
          50% { 
            opacity: 1; 
            transform: scale(1.01); 
          }
        }
      `}</style>
    </div>
  );
};

export default JourneyTherapyPreview;
